/**
 * PartitaDetailPage — dettaglio singola partita (link da home "Prossima sfida"
 * e dal calendario lega).
 *
 * Layout:
 *   - Header: nome competizione + giornata + squadre + risultato
 *   - Toggle CAMPO / LISTA (stile Fantagazzetta)
 *   - Due formazioni AFFIANCATE (md:grid-cols-2):
 *       - CAMPO: mini-pitch con badge ruolo + voto live + emoji bonus
 *       - LISTA: tabella con voto, gol, assist, ammoniz., espuls., autogol
 *   - Admin: bottone "Importa voti live ora" (manuale, fallback al cron)
 *
 * Voti per giocatore vengono da /giornate/{id}/voti-giocatori (refresh 15m).
 */

import { useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import {
  useCombinazioni,
  useFormazione,
  useGiornata,
  useRisultatiGiornata,
  useVotiGiocatoriGiornata,
} from "../../hooks/useFase4";
import type {
  CombinazioneItem,
  VotoGiocatoreItem,
  FormazioneItem,
  SostituzioneItem,
} from "../../hooks/useFase4";
import {
  useCompetizione,
  useCompetizioniStagione,
  usePartita,
  usePartiteGiornata,
  useRecapPartita,
} from "../../hooks/useFase5";
import { useRegolamento, useStagioneAttiva } from "../../hooks/useStagione";
import { useSquadre } from "../../hooks/useFase7";
import { useRosa } from "../../hooks/useFase2";
import type { RosaPlayer } from "../../hooks/useFase2";
import { api } from "../../lib/api";
import { MobileShell } from "../shell/MobileShell";
import { BottoneCondividi } from "../common/Condividi";
import { disegnaRisultato } from "../common/cardCondivisione";
import { BonusMalus, Pitch, type SlotState, type VotoLive } from "../formazione/Pitch";
import { MODULI_DISPONIBILI, type Modulo } from "../formazione/pitchLayouts";
import { BadgeRuoli } from "../common/BadgeRuoli";
import { etichetta } from "../common/etichette";
import { LogoSquadra } from "../common/LogoSquadra";
import { ModificatoriSquadra } from "./BloccoSquadra";
import { SelettorePartita } from "./SelettorePartita";
import { RecapPartita } from "./RecapPartita";
import { AvvisoRettifica, RettificaModal } from "./RettificaRisultato";
import { SimulazioneModal } from "./SimulazioneModal";
import { AvvisoModificaAdmin } from "../common/AvvisoModificaAdmin";
import { tr } from "../../i18n";

const ADMIN_ROLES = new Set(["presidente_lega", "admin", "mod_mercato"]);

function userIsAdmin(user: ReturnType<typeof useAuth>["user"]): boolean {
  if (!user) return false;
  const roles = new Set<string>([user.ruolo_lega ?? ""]);
  for (const r of user.ruoli_aggiuntivi ?? []) roles.add(r);
  for (const r of roles) if (ADMIN_ROLES.has(r)) return true;
  return false;
}

type ViewMode = "campo" | "lista";

function rosaToSlot(p: RosaPlayer | undefined): SlotState {
  if (!p) {
    return {
      giocatore_id: null,
      nome: null,
      ruoli_mantra: null,
      ruolo_classic: null,
    };
  }
  return {
    giocatore_id: p.giocatore_id,
    nome: p.nome,
    ruoli_mantra: p.ruoli_mantra,
    ruolo_classic: p.ruolo_classic,
  };
}

function isValidModulo(m: string | undefined): m is Modulo {
  return typeof m === "string" && (MODULI_DISPONIBILI as readonly string[]).includes(m);
}

function votoToLive(v: VotoGiocatoreItem): VotoLive {
  return {
    voto_base: v.voto_base,
    voto_finale: v.voto_finale,
    gol_fatti: v.gol_fatti,
    gol_subiti: v.gol_subiti,
    autogol: v.autogol,
    ammonizioni: v.ammonizioni,
    espulsioni: v.espulsioni,
    assist: v.assist,
    porta_inviolata: v.porta_inviolata,
    rigore_segnato: v.rigore_segnato,
    rigore_sbagliato: v.rigore_sbagliato,
    rigore_parato: v.rigore_parato,
  };
}

/** Array vuoto stabile: niente sostituzioni (giornata non calcolata o
 * squadra senza risultato). */
const NESSUNA_SOSTITUZIONE: SostituzioneItem[] = [];

/** True se il giocatore ha un voto valido (non SV). */
function hasValidVote(giocatoreId: string, votiById: Map<string, VotoGiocatoreItem>): boolean {
  const v = votiById.get(giocatoreId);
  return !!v && v.voto_base !== null && v.voto_base !== undefined;
}

interface FormationPanelProps {
  titolo: string;
  squadraNome: string;
  formazione: FormazioneItem | null | undefined;
  rosa: RosaPlayer[] | undefined;
  voti: VotoGiocatoreItem[] | undefined;
  combinazioni: CombinazioneItem[] | undefined;
  isMantra: boolean;
  view: ViewMode;
  /** Andrea 2026-09-13: OUT/IN compaiono solo a giornata calcolata;
   * mentre si gioca solo SV, perché le sostituzioni cambiano man mano
   * che arrivano i voti. */
  calcolata: boolean;
  /** Sostituzioni fatte davvero dal calcolo per questa squadra. */
  sostituzioni: SostituzioneItem[];
  /** Andrea 2026-10-15: chi ha preso il -1 fuori ruolo (dal recap), per
   * mostrarlo anche nella vista lista. */
  fuoriRuoloIds?: Set<string>;
  /** Andrea 2026-10-29: punti del regolamento per evento (gol → 3,
   * ammonizione → -0.5...): la lista mostra i punti, non i conteggi. */
  puntiBonus?: Map<string, number>;
}

function FormationPanel({
  titolo,
  squadraNome,
  formazione,
  rosa,
  voti,
  combinazioni,
  isMantra,
  view,
  calcolata,
  sostituzioni,
  fuoriRuoloIds,
  puntiBonus,
}: FormationPanelProps) {
  // TUTTI gli hooks devono essere chiamati incondizionatamente prima di
  // qualsiasi early return (rules of hooks — React error #310).
  const rosaById = useMemo(() => {
    const m = new Map<string, RosaPlayer>();
    for (const p of rosa ?? []) m.set(p.giocatore_id, p);
    return m;
  }, [rosa]);

  const votiById = useMemo(() => {
    const m = new Map<string, VotoGiocatoreItem>();
    for (const v of voti ?? []) m.set(v.giocatore_id, v);
    return m;
  }, [voti]);

  const votiLiveById = useMemo(() => {
    const m = new Map<string, VotoLive>();
    for (const v of voti ?? []) m.set(v.giocatore_id, votoToLive(v));
    return m;
  }, [voti]);

  // Andrea 2026-09-12: un club "ha giocato" quando ha voti caricati in
  // questa giornata (Andrea li importa partita per partita). Il club
  // arriva col voto; per i vecchi payload si ripiega sulla rosa.
  const clubConVoti = useMemo(() => {
    const s = new Set<string>();
    for (const v of voti ?? []) {
      const club = v.club_serie_a ?? rosaById.get(v.giocatore_id)?.club_serie_a;
      if (club) s.add(club);
    }
    return s;
  }, [voti, rosaById]);
  const partitaGiocata = useMemo(
    () => (giocatoreId: string) => {
      const club = rosaById.get(giocatoreId)?.club_serie_a;
      return calcolata || (!!club && clubConVoti.has(club));
    },
    [rosaById, clubConVoti, calcolata],
  );

  // Risolvi slotLabels PRIMA dell'early return (usa formazione?.modulo per
  // gestire safely il caso formazione null).
  const slotLabels: string[] | undefined = useMemo(() => {
    if (!isMantra || !formazione) return undefined;
    const exact = combinazioni?.find((c) => c.id === formazione.combinazione_id);
    if (exact) return exact.combinazione;
    const sameMod = combinazioni?.find((c) => c.modulo === formazione.modulo);
    if (sameMod) return sameMod.combinazione;
    return undefined;
  }, [combinazioni, formazione, isMantra]);

  // Andrea 2026-09-13: finché la giornata non è calcolata NIENTE
  // sostituzioni, solo SV: cambiano man mano che arrivano i voti (il
  // panchinaro che oggi entrerebbe domani può essere SV anche lui). A
  // giornata calcolata OUT/IN sono quelli veri del calcolo
  // (risultato.dettaglio_sostituzioni), non una stima fatta qui.
  const { effectiveIds, sostituzioniMap, ingressiIds } = useMemo(() => {
    const effective = new Set<string>();
    const mappa = new Map<string, string>();
    const ingressi = new Set<string>();
    if (!formazione) {
      return { effectiveIds: effective, sostituzioniMap: mappa, ingressiIds: ingressi };
    }
    if (calcolata) {
      for (const sost of sostituzioni) {
        mappa.set(sost.titolare_id, sost.sostituto_id);
        ingressi.add(sost.sostituto_id);
      }
    }
    for (const tid of formazione.titolari_ids) {
      if (mappa.has(tid)) continue;
      // Col voto, o con la partita ancora da giocare: negli 11.
      if (hasValidVote(tid, votiById) || !partitaGiocata(tid)) effective.add(tid);
    }
    for (const sid of ingressi) effective.add(sid);
    return { effectiveIds: effective, sostituzioniMap: mappa, ingressiIds: ingressi };
  }, [formazione, calcolata, sostituzioni, votiById, partitaGiocata]);

  // Senza voto a partita giocata = SV (titolari e panchinari). Chi deve
  // ancora giocare non e' SV: non ha ancora niente.
  const svIds = useMemo(() => {
    const s = new Set<string>();
    if (!formazione) return s;
    for (const id of [...formazione.titolari_ids, ...formazione.panchina_ids]) {
      if (!hasValidVote(id, votiById) && partitaGiocata(id)) s.add(id);
    }
    return s;
  }, [formazione, votiById, partitaGiocata]);

  // Andrea 2026-09-14: chi deve ancora giocare (giornata non calcolata,
  // club senza voti) mostra un 6 d'ufficio provvisorio, bianco.
  const daGiocareIds = useMemo(() => {
    const s = new Set<string>();
    if (!formazione || calcolata) return s;
    for (const id of [...formazione.titolari_ids, ...formazione.panchina_ids]) {
      if (hasValidVote(id, votiById) || partitaGiocata(id)) continue;
      const p = rosaById.get(id);
      if (!p?.club_serie_a || p.stato_giocatore !== "serie_a") continue;
      s.add(id);
    }
    return s;
  }, [formazione, calcolata, votiById, partitaGiocata, rosaById]);

  // Opachi: chi non concorre al voto E ha gia' un esito (voto preso ma
  // non entrato, oppure SV). Chi aspetta ancora la sua partita resta
  // luminoso senza etichette (Andrea 2026-09-12).
  const dimmedIds = useMemo(() => {
    if (!formazione) return new Set<string>();
    const out = new Set<string>();
    for (const id of [...formazione.titolari_ids, ...formazione.panchina_ids]) {
      if (effectiveIds.has(id)) continue;
      if (hasValidVote(id, votiById) || svIds.has(id)) out.add(id);
    }
    return out;
  }, [formazione, effectiveIds, votiById, svIds]);

  const outIds = useMemo(() => new Set<string>(sostituzioniMap.keys()), [sostituzioniMap]);

  if (!formazione) {
    return (
      <div className="card">
        <div className="font-mono text-[10px] uppercase tracking-wider text-fg-muted mb-1">
          {titolo}
        </div>
        <div className="font-display text-lg text-accent leading-tight break-words">
          {squadraNome}
        </div>
        <div className="text-sm text-fg-muted mt-2">{tr("Formazione non ancora schierata.")}</div>
      </div>
    );
  }

  const modulo: Modulo = isValidModulo(formazione.modulo) ? formazione.modulo : "4-3-3";

  const slots: SlotState[] = formazione.titolari_ids.map((id) => rosaToSlot(rosaById.get(id)));

  const titolari = formazione.titolari_ids
    .map((id) => rosaById.get(id))
    .filter((p): p is RosaPlayer => !!p);
  const panchina = formazione.panchina_ids
    .map((id) => rosaById.get(id))
    .filter((p): p is RosaPlayer => !!p);

  return (
    <div className="card space-y-2 p-3">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="font-mono text-[9px] uppercase tracking-wider text-fg-muted">
            {titolo}
          </div>
          <div className="font-display text-base text-accent leading-tight break-words">
            {squadraNome}
          </div>
        </div>
        <div className="text-[10px] text-fg-muted text-right shrink-0">
          <div className="font-mono">{formazione.modulo}</div>
          {formazione.consegnata_at && (
            <div className="text-success text-[9px]">{tr("✓ consegnata")}</div>
          )}
        </div>
      </div>

      {view === "campo" ? (
        <>
          <Pitch
            modulo={modulo}
            slots={
              slots.length === 11
                ? slots
                : [...slots, ...Array(11 - slots.length).fill(rosaToSlot(undefined))]
            }
            capitanoId={formazione.capitano_id ?? ""}
            viceId={formazione.vicecapitano_id ?? ""}
            slotLabels={slotLabels}
            votiPerGiocatore={votiLiveById}
            dimmedIds={dimmedIds}
            outIds={outIds}
            svIds={svIds}
            daGiocareIds={daGiocareIds}
            onDropToSlot={() => undefined}
            onClickPlayer={() => undefined}
          />
          {panchina.length > 0 && (
            <Bench
              players={panchina}
              votiById={votiById}
              ingressiIds={ingressiIds}
              dimmedIds={dimmedIds}
              svIds={svIds}
              daGiocareIds={daGiocareIds}
            />
          )}
          {/* Mini legenda colori — chiarisce chi conta per il voto */}
          <div className="flex items-center justify-center gap-3 text-[9px] text-fg-muted font-mono">
            <span className="flex items-center gap-1">
              <span className="inline-block w-2 h-2 rounded-full bg-accent" />
              {tr("negli 11")}
            </span>
            <span className="flex items-center gap-1 opacity-40">
              <span className="inline-block w-2 h-2 rounded-full bg-fg-muted" />
              {tr("SV / non entrato")}
            </span>
            {!calcolata && (
              <span className="flex items-center gap-1">
                <span className="inline-block w-2 h-2 rounded-full border border-fg-muted" />
                {tr("da giocare")}
              </span>
            )}
            {calcolata && (
              <span className="flex items-center gap-1">
                <span className="inline-block text-[8px] bg-success text-ink rounded px-0.5 font-bold">
                  IN
                </span>
                {tr("sostituto")}
              </span>
            )}
          </div>
        </>
      ) : (
        <>
          <VotiTable
            label="Titolari"
            players={titolari}
            votiById={votiById}
            capitanoId={formazione.capitano_id ?? ""}
            viceId={formazione.vicecapitano_id ?? ""}
            effectiveIds={effectiveIds}
            outIds={outIds}
            ingressiIds={ingressiIds}
            svIds={svIds}
            dimmedIds={dimmedIds}
            daGiocareIds={daGiocareIds}
            fuoriRuoloIds={fuoriRuoloIds}
            puntiBonus={puntiBonus}
          />
          {panchina.length > 0 && (
            <VotiTable
              label="Panchina"
              players={panchina}
              votiById={votiById}
              capitanoId={formazione.capitano_id ?? ""}
              viceId={formazione.vicecapitano_id ?? ""}
              effectiveIds={effectiveIds}
              outIds={outIds}
              ingressiIds={ingressiIds}
              svIds={svIds}
              dimmedIds={dimmedIds}
              daGiocareIds={daGiocareIds}
              fuoriRuoloIds={fuoriRuoloIds}
              puntiBonus={puntiBonus}
            />
          )}
        </>
      )}

      {/* Christian 2026-11-03: in fondo, cosi' i due campi affiancati
          restano allineati. */}
      <AvvisoModificaAdmin modifiche={formazione.modifiche_admin} />
    </div>
  );
}

interface BenchProps {
  players: RosaPlayer[];
  votiById: Map<string, VotoGiocatoreItem>;
  ingressiIds: Set<string>;
  dimmedIds: Set<string>;
  /** Ha giocato ma senza voto: qui si scrive "SV"; chi deve ancora
   * giocare non ha niente (Andrea 2026-09-12). */
  svIds: Set<string>;
  /** Deve ancora giocare: 6 d'ufficio provvisorio (Andrea 2026-09-14). */
  daGiocareIds: Set<string>;
}

/** Mini panchina sotto il campo in vista Campo. I sostituti entrati sono
 * "luminosi" con badge IN; gli altri opacizzati. */
function Bench({ players, votiById, ingressiIds, dimmedIds, svIds, daGiocareIds }: BenchProps) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-wider text-fg-muted font-mono mb-1 mt-1">
        {tr("Panchina")}
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-1.5">
        {players.map((p) => {
          const v = votiById.get(p.giocatore_id);
          const voto = v?.voto_finale ?? v?.voto_base;
          // Andrea 2026-10-22: un riquadro per ruolo, ognuno col suo colore,
          // e il nome intero sotto (prima era troncato "De Ketelae…").
          const isEntrato = ingressiIds.has(p.giocatore_id);
          const isDimmed = dimmedIds.has(p.giocatore_id);
          const isSv = svIds.has(p.giocatore_id);
          const isDaGiocare = daGiocareIds.has(p.giocatore_id);
          const inAttesa = voto == null && !isSv;
          return (
            <div
              key={p.giocatore_id}
              className={`relative rounded-card border bg-bg-card px-2 py-1.5 transition-opacity ${
                isEntrato ? "border-success/60 shadow-sm" : "border-primary-soft/40"
              } ${isDimmed ? "opacity-40" : "opacity-100"}`}
              title={
                isEntrato
                  ? tr("{{v1}} — entrato come sostituto", { v1: p.nome })
                  : inAttesa
                    ? tr("{{v1}} — partita ancora da giocare", { v1: p.nome })
                    : tr("{{v1}} — non entrato", { v1: p.nome })
              }
            >
              <div className="min-w-0">
                <BadgeRuoli ruoli={p.ruoli_mantra} classic={p.ruolo_classic} />
                <div className="text-[10px] font-bold leading-tight break-words mt-0.5">
                  {p.nome}
                </div>
              </div>
              <div className="flex items-center justify-between mt-0.5 gap-1">
                <span className="flex items-center gap-1 text-[9px] font-mono tabular-nums min-w-0">
                  {voto !== null && voto !== undefined ? (
                    <span className="rounded px-1 bg-primary-deep/90 font-bold text-accent">
                      {Number(voto).toFixed(1)}
                    </span>
                  ) : isSv ? (
                    <span className="text-fg-muted">SV</span>
                  ) : isDaGiocare ? (
                    <span
                      className="rounded px-1 bg-white text-[#2A0F4E] font-bold"
                      title={tr("Deve ancora giocare: 6 d'ufficio provvisorio")}
                    >
                      6.0
                    </span>
                  ) : (
                    <span className="text-fg-muted/50">&nbsp;</span>
                  )}
                  {/* Andrea 2026-09-14: bonus/malus anche in panchina */}
                  {v && (
                    <span className="flex items-center gap-0.5 text-[10px] leading-none">
                      <BonusMalus v={votoToLive(v)} />
                    </span>
                  )}
                </span>
                {isEntrato && (
                  <span className="text-[8px] font-bold bg-success text-ink px-1 rounded font-mono">
                    IN
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

interface VotiTableProps {
  label: string;
  players: RosaPlayer[];
  votiById: Map<string, VotoGiocatoreItem>;
  capitanoId: string;
  viceId: string;
  /** ID degli 11 effettivi (titolari validi + sostituti). Se non passato
   * la differenziazione opacita' viene saltata. */
  effectiveIds?: Set<string>;
  /** Titolari sostituiti (badge OUT). */
  outIds?: Set<string>;
  /** Panchinari entrati (badge IN). */
  ingressiIds?: Set<string>;
  /** Senza voto a partita giocata (Andrea 2026-09-12). */
  svIds?: Set<string>;
  /** Chi va opaco: se passato vince su effectiveIds. */
  dimmedIds?: Set<string>;
  /** Deve ancora giocare: 6 d'ufficio provvisorio (Andrea 2026-09-14). */
  daGiocareIds?: Set<string>;
  /** -1 fuori ruolo (Andrea 2026-10-15). */
  fuoriRuoloIds?: Set<string>;
  /** Punti del regolamento per evento; senza, restano i conteggi. */
  puntiBonus?: Map<string, number>;
}

function VotiTable({
  label,
  players,
  votiById,
  capitanoId,
  viceId,
  effectiveIds,
  outIds,
  ingressiIds,
  svIds,
  dimmedIds,
  daGiocareIds,
  fuoriRuoloIds,
  puntiBonus,
}: VotiTableProps) {
  // Andrea 2026-10-29: nelle colonne i PUNTI (gol → +3, ammonizione → -0.5)
  // invece di "1"/"2", cosi' il conto di bonus e malus e' gia' fatto. Senza
  // regolamento caricato restano i conteggi.
  const fmtPunti = (n: number) =>
    (n > 0 ? "+" : "") + (Number.isInteger(n) ? String(n) : n.toFixed(1));
  const cella = (n: number | undefined, evento: string, fallbackCls: string) => {
    if (!n) return <div />;
    const v = puntiBonus?.get(evento);
    if (v === undefined) {
      return <div className={`text-center font-mono text-[9px] ${fallbackCls}`}>{n}</div>;
    }
    const tot = n * v;
    return (
      <div
        className={`text-center font-mono text-[9px] font-bold tabular-nums ${
          tot > 0 ? "text-success" : tot < 0 ? "text-danger" : "text-fg-muted"
        }`}
        title={`${n} × ${fmtPunti(v)}`}
      >
        {fmtPunti(tot)}
      </div>
    );
  };
  return (
    <div>
      <div className="text-[9px] uppercase tracking-wider text-fg-muted font-mono mb-1">
        {label}
      </div>
      <div className="bg-primary-deep/30 rounded overflow-hidden">
        <div className="grid grid-cols-[1fr_2.5rem_1.4rem_1.4rem_1.4rem_1.4rem_1.4rem_1.4rem_1.4rem_1.4rem] gap-1 px-1.5 py-1 text-[9px] uppercase tracking-wider font-mono text-fg-muted border-b border-primary-soft">
          <div>{tr("Giocatore")}</div>
          <div className="text-right">{tr("Voto")}</div>
          <div className="text-center" title={tr("Gol fatti")}>
            G
          </div>
          <div className="text-center" title={tr("Assist")}>
            A
          </div>
          <div className="text-center" title={tr("Ammoniz/Espuls")}>
            ⚠
          </div>
          <div className="text-center" title={tr("Autogol")}>
            AG
          </div>
          <div className="text-center" title={tr("Gol subiti")}>
            GS
          </div>
          <div className="text-center" title={tr("Rigore segnato")}>
            Rf
          </div>
          <div className="text-center" title={tr("Rigore sbagliato")}>
            Rs
          </div>
          <div className="text-center" title={tr("Rigore parato")}>
            Rp
          </div>
        </div>
        {players.map((p) => {
          const v = votiById.get(p.giocatore_id);
          const isCap = p.giocatore_id === capitanoId;
          const isVice = p.giocatore_id === viceId;
          const voto = v?.voto_finale ?? v?.voto_base;
          const cartellini = (v?.ammonizioni ?? 0) + (v?.espulsioni ?? 0) * 2;
          // Differenziazione visiva: chi e' negli 11 effettivi → luminoso,
          // chi e' SV non sostituito o non entrato dalla panchina → opaco.
          const isEffective = dimmedIds
            ? !dimmedIds.has(p.giocatore_id)
            : effectiveIds == null || effectiveIds.has(p.giocatore_id);
          const isSv = svIds?.has(p.giocatore_id) === true;
          const isDaGiocare = daGiocareIds?.has(p.giocatore_id) === true;
          const isOut = outIds?.has(p.giocatore_id) === true;
          const isIn = ingressiIds?.has(p.giocatore_id) === true;
          return (
            <div
              key={p.giocatore_id}
              className={`grid grid-cols-[1fr_2.5rem_1.4rem_1.4rem_1.4rem_1.4rem_1.4rem_1.4rem_1.4rem_1.4rem] gap-1 px-1.5 py-1 text-[11px] border-b border-primary-soft/40 last:border-b-0 items-center transition-opacity ${
                isEffective ? "opacity-100" : "opacity-40"
              }`}
            >
              {/* Andrea 2026-10-22: tutti i ruoli, staccati, sotto il nome
                  (prima una colonna col solo primo ruolo). */}
              <div className="min-w-0">
                <div className="flex items-center gap-1 min-w-0">
                  <span className="truncate font-bold">{p.nome}</span>
                  {isCap && (
                    <span className="font-mono text-[8px] bg-accent text-ink rounded px-0.5">
                      C
                    </span>
                  )}
                  {isVice && (
                    <span className="font-mono text-[8px] bg-info text-ink rounded px-0.5">V</span>
                  )}
                  {isOut && (
                    <span
                      className="font-mono text-[8px] bg-danger text-fg rounded px-0.5"
                      title={tr("Sostituito (SV)")}
                    >
                      OUT
                    </span>
                  )}
                  {isIn && (
                    <span
                      className="font-mono text-[8px] bg-success text-ink rounded px-0.5"
                      title={tr("Entrato come sostituto")}
                    >
                      IN
                    </span>
                  )}
                  {fuoriRuoloIds?.has(p.giocatore_id) && (
                    <span
                      className="font-mono text-[8px] bg-danger/80 text-white rounded px-0.5"
                      title={tr("Fuori ruolo: -1")}
                    >
                      -1
                    </span>
                  )}
                </div>
                <BadgeRuoli ruoli={p.ruoli_mantra} classic={p.ruolo_classic} className="mt-0.5" />
              </div>
              <div className="text-right font-mono tabular-nums">
                {voto !== null && voto !== undefined ? (
                  <span className={v?.voto_finale ? "font-bold text-accent" : "text-fg"}>
                    {Number(voto).toFixed(1)}
                  </span>
                ) : isSv ? (
                  <span className="text-fg-muted">SV</span>
                ) : isDaGiocare ? (
                  <span
                    className="rounded px-1 bg-white text-[#2A0F4E] font-bold"
                    title={tr("Deve ancora giocare: 6 d'ufficio provvisorio")}
                  >
                    6.0
                  </span>
                ) : (
                  <span className="text-fg-muted">—</span>
                )}
              </div>
              {(() => {
                const isGk = p.ruolo_classic === "P" || !!p.ruoli_mantra?.includes("Por");
                const evGol = isGk && puntiBonus?.has("gol_portiere") ? "gol_portiere" : "gol";
                return (
                  <>
                    {cella(v?.gol_fatti, evGol, "text-success")}
                    {cella(v?.assist, "assist", "text-info")}
                    {cartellini > 0 ? (
                      puntiBonus?.has(v?.espulsioni ? "espulsione" : "ammonizione") ? (
                        cella(
                          v?.espulsioni ? v.espulsioni : v?.ammonizioni,
                          v?.espulsioni ? "espulsione" : "ammonizione",
                          "",
                        )
                      ) : (
                        <div className="text-center font-mono text-[10px]">
                          <span className={v?.espulsioni ? "text-danger" : "text-warning"}>
                            {v?.espulsioni ? "🟥" : "🟨"}
                          </span>
                        </div>
                      )
                    ) : (
                      <div />
                    )}
                    {cella(v?.autogol, "autogoal", "text-danger")}
                    {/* gol subiti: malus solo per il portiere */}
                    {isGk ? (
                      cella(v?.gol_subiti, "gol_subito_portiere", "text-danger")
                    ) : (
                      <div className="text-center font-mono text-[9px] text-fg-muted/60">
                        {v?.gol_subiti ? v.gol_subiti : ""}
                      </div>
                    )}
                    {cella(v?.rigore_segnato, "rigore_segnato", "text-success")}
                    {cella(v?.rigore_sbagliato, "rigore_sbagliato", "text-danger")}
                    {cella(v?.rigore_parato, "rigore_parato", "text-info")}
                  </>
                );
              })()}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function PartitaDetailPage() {
  const { user } = useAuth();
  const { partitaId } = useParams<{ partitaId: string }>();
  const isAdmin = userIsAdmin(user);

  const [view, setView] = useState<ViewMode>("campo");
  // Andrea 2026-09-13: modal dell'esito provvisorio (montato solo aperto).
  const [simulazioneAperta, setSimulazioneAperta] = useState(false);
  // Andrea 2026-09-15: modifica manuale del risultato. Solo presidente e
  // admin, come nel backend (qui mod_mercato non basta).
  const [rettificaAperta, setRettificaAperta] = useState(false);
  const puoRettificare =
    !!user &&
    [user.ruolo_lega, ...(user.ruoli_aggiuntivi ?? [])].some(
      (r) => r === "presidente_lega" || r === "admin",
    );

  const { data: partita, isLoading: loadingPartita } = usePartita(partitaId ?? null);
  const { data: competizione } = useCompetizione(partita?.competizione_id ?? null);
  const { data: squadre } = useSquadre();
  const { data: stagioneAttiva } = useStagioneAttiva();

  const stagioneId = competizione?.stagione_id ?? null;
  const giornataId = partita?.giornata_id ?? null;
  // Andrea 2026-10-29: punti del regolamento per evento, per la vista lista
  const { data: regolamento } = useRegolamento(stagioneId);
  const puntiBonus = useMemo(() => {
    const m = new Map<string, number>();
    for (const b of regolamento?.bonus_malus ?? []) m.set(b.evento, Number(b.punteggio));
    return m.size > 0 ? m : undefined;
  }, [regolamento]);

  const isMantra = stagioneAttiva?.modalita === "mantra";
  const { data: combinazioni } = useCombinazioni(isMantra ? "mantra" : "classic");

  const squadraCasaId = partita?.squadra_casa_id ?? null;
  const squadraTrasfertaId = partita?.squadra_trasferta_id ?? null;

  // Andrea 2026-10-30: la formazione di QUESTA competizione, come la usa il
  // calcolo (se manca, l'ultima consegnata di qualunque competizione).
  const competizioneId = partita?.competizione_id ?? null;
  const { data: formazioneCasa } = useFormazione(squadraCasaId, giornataId, competizioneId, true);
  const { data: formazioneTrasferta } = useFormazione(
    squadraTrasfertaId,
    giornataId,
    competizioneId,
    true,
  );
  const { data: rosaCasa } = useRosa(stagioneId, squadraCasaId);
  const { data: rosaTrasferta } = useRosa(stagioneId, squadraTrasfertaId);

  const { data: voti, refetch: refetchVoti } = useVotiGiocatoriGiornata(giornataId);
  // Andrea 2026-09-20: menu in cima per saltare al live di un'altra
  // partita della stessa giornata, senza passare dal calendario.
  const { data: partiteGiornata } = usePartiteGiornata(giornataId);
  const { data: competizioniStagione } = useCompetizioniStagione(stagioneId);
  // Andrea 2026-09-13: OUT/IN solo a giornata calcolata, presi dal calcolo.
  const { data: giornataInfo } = useGiornata(giornataId);
  const { data: risultati } = useRisultatiGiornata(giornataId);
  const giornataCalcolata = giornataInfo?.stato === "calcolata";
  // Andrea 2026-09-17: a giornata calcolata la testata mostra anche i
  // modificatori di squadra. Stessa query del recap sotto il campo: una
  // chiamata sola.
  const { data: recap } = useRecapPartita(partita?.id ?? null, giornataCalcolata);
  // Andrea 2026-10-15: il -1 fuori ruolo si vede anche nella vista lista
  const fuoriRuoloCasa = useMemo(
    () =>
      new Set(
        (recap?.casa.giocatori ?? []).filter((g) => g.malus_fuori_ruolo).map((g) => g.giocatore_id),
      ),
    [recap],
  );
  const fuoriRuoloTrasferta = useMemo(
    () =>
      new Set(
        (recap?.trasferta.giocatori ?? [])
          .filter((g) => g.malus_fuori_ruolo)
          .map((g) => g.giocatore_id),
      ),
    [recap],
  );
  // Andrea 2026-10-30: il risultato di questa competizione (le righe
  // storiche senza competizione valgono per tutte).
  const sostituzioniDi = (squadraId: string | null): SostituzioneItem[] => {
    if (!squadraId) return NESSUNA_SOSTITUZIONE;
    const miei = risultati?.filter((r) => r.squadra_id === squadraId) ?? [];
    const r =
      miei.find((x) => x.competizione_id === competizioneId) ??
      miei.find((x) => !x.competizione_id) ??
      miei[0];
    return r?.dettaglio_sostituzioni ?? NESSUNA_SOSTITUZIONE;
  };

  const [importMsg, setImportMsg] = useState<string | null>(null);
  const importVotiMut = useMutation({
    mutationFn: async () => {
      if (!giornataId) throw new Error(tr("Nessuna giornata"));
      // Timeout esteso: l'import puo' richiedere fino a ~1 min se ci sono
      // 10 partite da scaricare con eventuale retry su rate-limit
      // football-data (tier free 10 req/min).
      const res = await api.post<{
        eventi: number;
        voti_calcolati: number;
        voti_inseriti: number;
        non_abbinati: number;
        partite_total: number;
        partite_scheduled: number;
        partite_in_play: number;
        partite_finished: number;
        partite_postponed: number;
        nota: string;
        provider: string;
      }>(
        `/admin/voti-live/importa/${giornataId}`,
        null,
        { timeout: 120000 }, // 2 min
      );
      return res.data;
    },
    onSuccess: (data) => {
      const partite =
        tr("[provider: {{v1}}] Partite: {{v2}} ", { v1: data.provider, v2: data.partite_total }) +
        tr("(🟢 {{v1}} fin · 🔴 {{v2}} live · ", {
          v1: data.partite_finished,
          v2: data.partite_in_play,
        }) +
        tr("⏳ {{v1}} programmate", { v1: data.partite_scheduled }) +
        (data.partite_postponed > 0
          ? tr(" · ⏸ {{v1}} rinviate", { v1: data.partite_postponed })
          : "") +
        ").";
      const main =
        data.voti_inseriti > 0
          ? tr("✓ {{v1}} voti inseriti su {{v2}} calcolati da {{v3}} eventi.", {
              v1: data.voti_inseriti,
              v2: data.voti_calcolati,
              v3: data.eventi,
            })
          : tr("ℹ 0 voti inseriti. {{v1}}", { v1: data.nota || "Nessun evento disponibile." });
      const naMsg =
        data.non_abbinati > 0
          ? tr(" {{v1}} nomi non abbinati ai giocatori in DB.", { v1: data.non_abbinati })
          : "";
      setImportMsg(tr("{{v1}} {{v2}}{{v3}}", { v1: main, v2: partite, v3: naMsg }));
      refetchVoti();
    },
    onError: (e: {
      response?: { data?: { detail?: string }; status?: number };
      message?: string;
      code?: string;
    }) => {
      const detail = e.response?.data?.detail;
      const status = e.response?.status;
      const code = e.code;
      let msg: string;
      if (detail) {
        msg = tr("{{v1}} (HTTP {{v2}})", { v1: detail, v2: status ?? "?" });
      } else if (code === "ECONNABORTED" || e.message?.includes("timeout")) {
        msg =
          "Timeout: l'import ha richiesto > 2 min. Riprova fra qualche secondo (probabile rate-limit API).";
      } else if (e.message === "Network Error" || code === "ERR_NETWORK") {
        msg =
          tr("Network error: il backend non ha risposto. Possibili cause: ") +
          tr("redeploy in corso su Railway (riprova fra 30 sec), oppure 5xx ") +
          tr("non gestito. Controlla la tab Network del browser.");
      } else {
        msg = e.message ?? tr("errore sconosciuto");
      }
      setImportMsg(`⚠ Errore import: ${msg}`);
    },
  });

  if (loadingPartita || !partita) {
    return (
      <MobileShell logoSuffix="PARTITA">
        <div className="card text-fg-muted">{tr("Caricamento partita…")}</div>
      </MobileShell>
    );
  }

  const squadraNome = (id: string): string =>
    squadre?.find((s) => s.id === id)?.nome ?? id.slice(0, 8);
  const nomeCasa = squadraNome(partita.squadra_casa_id);
  const nomeTrasferta = squadraNome(partita.squadra_trasferta_id);

  const tipoVoti = voti?.[0]?.tipo;
  const haVoti = voti && voti.length > 0;
  const haRisultato = partita.punteggio_casa !== null && partita.punteggio_casa !== undefined;

  // Andrea 2026-10-08: il risultato come immagine + testo da condividere.
  const preparaCondivisione = async () => {
    const logo = (id: string) => squadre?.find((s) => s.id === id)?.logo_data_url ?? null;
    const intestazione = `${competizione?.nome ?? "FantaSquad"}${
      giornataInfo?.numero ? tr(" · Giornata {{v1}}", { v1: giornataInfo.numero }) : ""
    }`;
    const fpCasa = haRisultato ? Number(partita.punteggio_casa) : null;
    const fpTrasf = haRisultato ? Number(partita.punteggio_trasferta) : null;
    const provvisorio = !giornataCalcolata;
    const blob = await disegnaRisultato({
      intestazione,
      casa: {
        nome: nomeCasa,
        logoDataUrl: logo(partita.squadra_casa_id),
        gol: partita.gol_casa,
        fantapunti: fpCasa,
      },
      trasferta: {
        nome: nomeTrasferta,
        logoDataUrl: logo(partita.squadra_trasferta_id),
        gol: partita.gol_trasferta,
        fantapunti: fpTrasf,
      },
      nota: provvisorio ? tr("Risultato provvisorio: giornata non ancora calcolata") : null,
    });
    const fp =
      fpCasa !== null && fpTrasf !== null
        ? tr(" ({{v1}} · {{v2}} FP)", { v1: fpCasa.toFixed(1), v2: fpTrasf.toFixed(1) })
        : "";
    const testo = `⚽ ${nomeCasa} ${partita.gol_casa}-${partita.gol_trasferta} ${nomeTrasferta}${fp}\n${intestazione}${
      provvisorio ? tr("\nRisultato provvisorio") : ""
    }\nfantasquad.it`;
    return {
      blob,
      nomeFile: `risultato-${nomeCasa}-${nomeTrasferta}.png`.replace(/\s+/g, "-"),
      titolo: tr("{{v1}} {{v2}}-{{v3}} {{v4}}", {
        v1: nomeCasa,
        v2: partita.gol_casa,
        v3: partita.gol_trasferta,
        v4: nomeTrasferta,
      }),
      testo,
    };
  };

  return (
    <MobileShell logoSuffix="PARTITA">
      <SelettorePartita
        partita={partita}
        partite={partiteGiornata}
        competizioni={competizioniStagione}
        nomeSquadra={squadraNome}
        giornata={giornataInfo?.numero ?? null}
      />

      {/* Header risultato */}
      <div className="card space-y-2">
        <div className="text-center">
          <div className="font-mono text-[10px] uppercase tracking-wider text-accent">
            {competizione?.nome ?? tr("Competizione")}
            {competizione?.tipo ? tr(" · {{v1}}", { v1: etichetta(competizione.tipo) }) : ""}
          </div>
        </div>
        {/* Andrea 2026-10-30: loghi in riga 1, nomi in riga 2 (un nome lungo
            non sposta il logo); il risultato occupa entrambe le righe. */}
        <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-x-3 gap-y-1">
          <div className="flex justify-center">
            <LogoSquadra squadraId={partita.squadra_casa_id} size={48} />
          </div>
          <div className="row-span-2 text-center">
            <div className="font-display text-3xl tabular-nums">
              {partita.punteggio_casa !== null && partita.punteggio_casa !== undefined
                ? tr("{{v1}} - {{v2}}", { v1: partita.gol_casa, v2: partita.gol_trasferta })
                : "— vs —"}
            </div>
            {partita.punteggio_casa !== null && partita.punteggio_casa !== undefined && (
              <div className="text-[10px] text-fg-muted font-mono mt-1">
                {Number(partita.punteggio_casa).toFixed(1)} ·{" "}
                {Number(partita.punteggio_trasferta).toFixed(1)} FP
              </div>
            )}
          </div>
          <div className="flex justify-center">
            <LogoSquadra squadraId={partita.squadra_trasferta_id} size={48} />
          </div>
          <div className="text-center min-w-0 self-start">
            <div className="font-display text-base sm:text-lg text-fg leading-tight break-words">
              {nomeCasa}
            </div>
            <div className="text-[10px] text-fg-muted">CASA</div>
          </div>
          <div className="text-center min-w-0 self-start">
            <div className="font-display text-base sm:text-lg text-fg leading-tight break-words">
              {nomeTrasferta}
            </div>
            <div className="text-[10px] text-fg-muted">{tr("TRASFERTA")}</div>
          </div>
        </div>
        {/* Andrea 2026-09-17: a partita conclusa, sotto il risultato, i
            modificatori di ogni squadra (capitano, D-Factor, fairplay,
            fuori ruolo). Il dettaglio completo resta nel recap in fondo. */}
        {giornataCalcolata && recap && (
          <div className="grid grid-cols-2 gap-3">
            <ModificatoriSquadra s={recap.casa} />
            <ModificatoriSquadra s={recap.trasferta} />
          </div>
        )}
        {/* Andrea 2026-09-15: risultato corretto a mano da un admin */}
        {partita.rettifica && <AvvisoRettifica rettifica={partita.rettifica} />}
        {/* Andrea 2026-10-08: manda il risultato su WhatsApp/Telegram/Instagram */}
        {haRisultato && (
          <BottoneCondividi
            genera={preparaCondivisione}
            etichetta="Condividi il risultato"
            className="btn-ghost w-full text-sm"
            title={tr("Manda il risultato come immagine su WhatsApp, Telegram, Instagram…")}
          />
        )}
        {haVoti && (
          <div className="flex items-center justify-center gap-2">
            <div
              className={`text-xs ${
                tipoVoti === "live" ? "text-warning animate-pulse" : "text-success"
              }`}
            >
              {tipoVoti === "live" ? tr("🔴 Voti LIVE (in aggiornamento)") : tr("✓ Voti finali")}
            </div>
            <button
              type="button"
              onClick={() => refetchVoti()}
              className="text-[10px] text-fg-muted hover:text-accent underline"
              title={tr("Forza refresh voti adesso")}
            >
              {tr("↻ aggiorna")}
            </button>
          </div>
        )}
        {!haVoti && (
          <div className="flex items-center justify-center">
            <button
              type="button"
              onClick={() => refetchVoti()}
              className="text-[10px] text-fg-muted hover:text-accent underline"
              title={tr("Forza refresh voti adesso")}
            >
              {tr("↻ Aggiorna voti")}
            </button>
          </div>
        )}
        <div className="text-center text-[10px] text-fg-muted">
          <Link to="/lega" className="hover:text-accent">
            {tr("Vai alla classifica →")}
          </Link>
        </div>
      </div>

      {/* Andrea 2026-09-13: esito provvisorio con i voti di adesso. Solo
          finche' la giornata non e' calcolata; non salva niente. */}
      {!giornataCalcolata && partita.giornata_id && (
        <div className="flex justify-center">
          <button
            type="button"
            onClick={() => setSimulazioneAperta(true)}
            className="px-4 py-2 rounded-card bg-accent text-ink font-bold text-sm tracking-wide shadow hover:opacity-90 transition"
          >
            {tr("🔮 Calcola esito provvisorio")}
          </button>
        </div>
      )}

      {/* Andrea 2026-09-15: a giornata calcolata presidente e admin possono
          correggere a mano il risultato (vale anche per la classifica). */}
      {giornataCalcolata && puoRettificare && (
        <div className="flex justify-center">
          <button
            type="button"
            onClick={() => setRettificaAperta(true)}
            className="btn-ghost text-sm border border-warning/40 text-warning hover:bg-warning/10"
          >
            {tr("✎ Modifica risultato")}
          </button>
        </div>
      )}

      {/* Toggle Campo / Lista */}
      <div className="flex gap-2 justify-center">
        <button
          type="button"
          onClick={() => setView("campo")}
          className={`pill ${
            view === "campo"
              ? "bg-accent text-ink"
              : "bg-primary-deep/40 text-fg-muted hover:bg-primary-deep/60"
          }`}
        >
          {tr("⚽ Campo")}
        </button>
        <button
          type="button"
          onClick={() => setView("lista")}
          className={`pill ${
            view === "lista"
              ? "bg-accent text-ink"
              : "bg-primary-deep/40 text-fg-muted hover:bg-primary-deep/60"
          }`}
        >
          {tr("📋 Lista")}
        </button>
      </div>

      {/* Due formazioni AFFIANCATE (stile fantagazzetta) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <FormationPanel
          titolo="🏠 CASA"
          squadraNome={nomeCasa}
          calcolata={giornataCalcolata}
          puntiBonus={puntiBonus}
          sostituzioni={sostituzioniDi(squadraCasaId)}
          fuoriRuoloIds={fuoriRuoloCasa}
          formazione={formazioneCasa}
          rosa={rosaCasa?.items}
          voti={voti}
          combinazioni={combinazioni}
          isMantra={isMantra}
          view={view}
        />
        <FormationPanel
          titolo="✈️ TRASFERTA"
          squadraNome={nomeTrasferta}
          calcolata={giornataCalcolata}
          puntiBonus={puntiBonus}
          sostituzioni={sostituzioniDi(squadraTrasfertaId)}
          fuoriRuoloIds={fuoriRuoloTrasferta}
          formazione={formazioneTrasferta}
          rosa={rosaTrasferta?.items}
          voti={voti}
          combinazioni={combinazioni}
          isMantra={isMantra}
          view={view}
        />
      </div>

      {/* Andrea 2026-09-15: a partita conclusa, sotto il campo, il dettaglio
          del calcolo come nell'esito provvisorio (voti, capitano, D-Factor),
          per tutti. */}
      {giornataCalcolata && (
        <RecapPartita partita={partita} nomeCasa={nomeCasa} nomeTrasferta={nomeTrasferta} />
      )}

      {/* Admin app: import voti (comune a tutte le leghe, Andrea 29/09) */}
      {user?.is_admin_app && giornataId && (
        <div className="card bg-info/5 border border-info/30 space-y-2">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="text-xs text-fg-muted">
              🔧 <strong className="text-info">{tr("Tool admin")}</strong>: il cron auto-import gira
              ogni 15 min. Usa questo bottone per forzare ora se le partite sono iniziate e non vedi
              voti.
            </div>
            <div className="flex gap-2 flex-wrap">
              <Link
                to="/admin/carica-voti"
                className="btn-ghost text-sm border border-accent/40 text-accent hover:bg-accent/10"
              >
                {tr("✍ Import manuale (paste/CSV/Gazzetta)")}
              </Link>
              <button
                type="button"
                disabled={importVotiMut.isPending}
                onClick={() => importVotiMut.mutate()}
                className="btn-ghost text-sm border border-info/40 text-info hover:bg-info/10 disabled:opacity-50"
              >
                {importVotiMut.isPending ? tr("Import…") : tr("📥 Importa voti live ora")}
              </button>
            </div>
          </div>
          {importMsg && (
            <div
              className={`text-xs whitespace-pre-wrap ${
                importMsg.startsWith("✓")
                  ? "text-success"
                  : importMsg.startsWith("ℹ")
                    ? "text-info"
                    : "text-danger"
              }`}
            >
              {importMsg}
            </div>
          )}
        </div>
      )}

      {!haVoti && (
        <div className="card bg-info/10 border border-info/30 text-info text-sm text-center">
          ℹ Nessun voto disponibile per questa giornata. Il cron live importa ogni 15 min se ci sono
          partite in corso.
          {isAdmin && tr(" Usa il bottone admin sopra per triggerare ora.")}
        </div>
      )}
      {simulazioneAperta && (
        <SimulazioneModal
          partitaId={partita.id}
          nomeCasa={nomeCasa}
          nomeTrasferta={nomeTrasferta}
          onClose={() => setSimulazioneAperta(false)}
        />
      )}
      {rettificaAperta && (
        <RettificaModal
          partita={partita}
          nomeCasa={nomeCasa}
          nomeTrasferta={nomeTrasferta}
          stagioneId={stagioneId}
          onClose={() => setRettificaAperta(false)}
        />
      )}
    </MobileShell>
  );
}
